import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import Pagination from '../components/shared/Pagination';
import FilterBar, { dateRangeForFilter, DEFAULT_DATE_PRESETS } from '../components/shared/FilterBar';
import { useResizableColumns, ResizeHandle, SortableHeader } from '../components/shared/tableUtils';

// Booking Date = `created_at` (a full timestamp). It must render on the
// Pacific business calendar (matching Scale Portal + the BE created_at
// bucketing), NOT the browser's local timezone — otherwise a late-UTC
// created_at (e.g. 2026-04-15T23:18Z) shows Apr 16 in UTC+5 but Apr 15
// in US/Pacific, drifting the column off the day used by the date filter.
function fmtBookingDate(raw) {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'America/Los_Angeles',
  });
}

/* ── Static Data (fallback) ── */
const defaultCasesData = [
  {
    id: 'MED-001', mediator: 'Lisa Rodriguez', caseName: 'John Smith vs ABC Realty',
    date: 'March 12, 2026',
    plaintiff: { onboarding: 'green', payment: 'green', brief: 'green' },
    defendant: { onboarding: 'green', payment: 'green', brief: 'green' },
  },
  {
    id: 'MED-002', mediator: 'Michael Chen', caseName: 'Property LLC vs Jane Doe',
    date: 'March 12, 2026',
    plaintiff: { onboarding: 'green', payment: 'green', brief: 'red' },
    defendant: { onboarding: 'green', payment: 'red', brief: 'red' },
  },
  {
    id: 'MED-003', mediator: 'Lisa Rodriguez', caseName: 'Residential Co vs Bob Wilson',
    date: 'March 12, 2026',
    plaintiff: { onboarding: 'green', payment: 'green', brief: 'red' },
    defendant: { onboarding: 'green', payment: 'green', brief: 'green' },
  },
  {
    id: 'MED-004', mediator: 'Robert Anderson', caseName: 'Sarah Johnson vs Premier Properties',
    date: 'March 12, 2026',
    plaintiff: { onboarding: 'green', payment: 'red', brief: 'red' },
    defendant: { onboarding: 'green', payment: 'red', brief: 'green' },
  },
  {
    id: 'MED-005', mediator: 'Jennifer Wu', caseName: 'Martin Homes vs Grand Montage',
    date: 'March 12, 2026',
    plaintiff: { onboarding: 'green', payment: 'green', brief: 'green' },
    defendant: { onboarding: 'green', payment: 'red', brief: 'green' },
  },
  {
    id: 'MED-006', mediator: 'Robert Anderson', caseName: 'Emma Thompson vs Coastal Realty',
    date: 'March 12, 2026',
    plaintiff: { onboarding: 'green', payment: 'green', brief: 'red' },
    defendant: { onboarding: 'green', payment: 'red', brief: 'red' },
  },
  {
    id: 'MED-007', mediator: 'Jennifer Wu', caseName: 'Karen Wells vs Pacific Holdings',
    date: 'March 12, 2026',
    plaintiff: { onboarding: 'green', payment: 'green', brief: 'red' },
    defendant: { onboarding: 'green', payment: 'green', brief: 'green' },
  },
  {
    id: 'MED-008', mediator: 'Michael Chen', caseName: 'Davis Corp vs Mountain View LLC',
    date: 'March 12, 2026',
    plaintiff: { onboarding: 'green', payment: 'green', brief: 'green' },
    defendant: { onboarding: 'green', payment: 'green', brief: 'green' },
  },
  {
    id: 'MED-009', mediator: 'Lisa Rodriguez', caseName: 'Rivera Family vs Sunset Properties',
    date: 'March 12, 2026',
    plaintiff: { onboarding: 'green', payment: 'green', brief: 'red' },
    defendant: { onboarding: 'green', payment: 'red', brief: 'red' },
  },
  {
    id: 'MED-010', mediator: 'Robert Anderson', caseName: 'Chang Industries vs Harbor Realty',
    date: 'March 12, 2026',
    plaintiff: { onboarding: 'green', payment: 'green', brief: 'red' },
    defendant: { onboarding: 'green', payment: 'green', brief: 'green' },
  },
];

/* ── Sub-components ── */
// Keep this map in sync with Dashboard.jsx so the same case shows identical
// traffic-light colors on both pages.
const STATUS_COLOR_MAP = {
  green: '#22c55e',
  yellow: '#f59e0b',
  red: '#ef4444',
  grey: '#f59e0b',
};

function StatusDot({ color, label, statusText }) {
  const dotColor = STATUS_COLOR_MAP[color] || STATUS_COLOR_MAP.red;
  return (
    <span
      className="inline-flex items-center gap-1"
      title={statusText ? `${label}: ${statusText}` : label}
    >
      <span
        className="w-2 h-2 rounded-full"
        style={{ backgroundColor: dotColor }}
      />
      <span className="text-[11px] text-[#5b5a64] font-normal" style={{ fontFamily: 'Inter' }}>
        {label}
      </span>
    </span>
  );
}

function StatusCell({ status, briefLabel = 'Brief' }) {
  const s = status || { onboarding: 'grey', payment: 'grey', brief: 'grey' };
  return (
    <div className="flex items-center gap-3">
      <StatusDot color={s.onboarding} statusText={s.onboardingStatus} label="Onboarding" />
      <StatusDot color={s.payment} statusText={s.paymentStatus} label="Payment" />
      <StatusDot color={s.brief} statusText={s.briefStatus} label={briefLabel} />
    </div>
  );
}

function ActionIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <circle cx="7" cy="3.5" r="1" fill="#5b5a64" />
      <circle cx="7" cy="7" r="1" fill="#5b5a64" />
      <circle cx="7" cy="10.5" r="1" fill="#5b5a64" />
    </svg>
  );
}

function KPICard({ title, value, change, periodLabel }) {
  // Mirrors the Dashboard's ChangeBadge so the comparison label tracks
  // the active FilterBar range.
  const dir = change?.direction || 'flat';
  const pct = change?.percent;
  const isNew = change?.isNew;
  const compareLabel = change?.compareLabel || 'last month';
  const isDown = dir === 'down';
  const isFlat = dir === 'flat';
  const bg = isFlat ? '#f3f4f6' : (isDown ? '#fde8e9' : '#e8fdf5');
  const color = isFlat ? '#6b7280' : (isDown ? '#a60003' : '#00a666');
  let label;
  if (isNew) {
    label = `+${change.value} new`;
  } else if (typeof pct === 'number') {
    const sign = pct > 0 ? '+' : '';
    label = `${sign}${pct}%`;
  } else {
    label = '—';
  }
  return (
    <div className="flex flex-col bg-white rounded-[10px] border border-[#e5e5e5] overflow-hidden flex-1">
      <div className="flex items-center gap-2 px-3.5 py-3 border-b border-[#e5e5e5]">
        <span className="text-xs font-medium text-[#0e1e20]" style={{ fontFamily: 'Inter Tight' }}>
          {title}
        </span>
        {periodLabel ? (
          <span
            className="text-[10px] font-semibold text-gray-600 bg-gray-100 rounded-md whitespace-nowrap"
            style={{ padding: '3px 7px', fontFamily: 'Inter Tight' }}
          >
            {periodLabel}
          </span>
        ) : null}
      </div>
      <div className="flex flex-col items-end justify-end gap-3.5 px-3.5 pt-5 pb-3.5">
        <span className="text-[30px] font-medium text-[#0e1e20] leading-none self-start" style={{ fontFamily: 'Inter Tight' }}>
          {value}
        </span>
        <span
          className="inline-flex items-center gap-[5px] rounded-full"
          style={{ backgroundColor: bg, padding: '4px 8px 5px 8px' }}
        >
          {isDown ? (
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M1.5 3.5L4.5 6.5L6.5 4.5L10.5 8.5" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M7.5 8.5H10.5V5.5" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          ) : (
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M1.5 8.5L4.5 5.5L6.5 7.5L10.5 3.5" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M7.5 3.5H10.5V6.5" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
          <span className="flex items-center gap-[3px]">
            <span className="text-[10px] font-medium" style={{ fontFamily: 'Inter Tight', color }}>{label}</span>
            <span className="text-[10px] font-medium text-[#0e1e20]" style={{ fontFamily: 'Inter Tight' }}>vs</span>
            <span className="text-[10px] font-medium text-[#0e1e20]" style={{ fontFamily: 'Inter Tight' }}>{compareLabel}</span>
          </span>
        </span>
      </div>
    </div>
  );
}

/* ── Main Component ── */
export default function Cases() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const initials = ((user?.firstName || 'U')[0] + ((user?.lastName || '')[0] || '')).toUpperCase();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  // Server-side sort.  Default: most recent mediation_date first.  Without
  // server-side sort the previous useSortableData hook only re-ordered the
  // current page of rows — sort + pagination produced incorrect results
  // whenever the dataset spanned more than one page.
  const [sortBy, setSortBy] = useState('mediation_date');
  // Default: mediation date ascending (earliest mediation date first).
  const [sortDir, setSortDir] = useState('asc');
  const [casesData, setCasesData] = useState([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dashboardStats, setDashboardStats] = useState(null);
  const [mediatorsList, setMediatorsList] = useState([]);
  const [actionMenuId, setActionMenuId] = useState(null);
  // If the user landed here via a chart-click drill-in (Dashboard ⇒
  // Cases?startDate=…&endDate=…&mediatorId=…), seed the FilterBar with
  // those values so the table immediately shows the right slice.
  const [searchParams] = useSearchParams();
  const initialStart = searchParams.get('startDate') || '';
  const initialEnd = searchParams.get('endDate') || '';
  // Honor a ?mediatorId=… deep-link. Without this seed, drill-downs from
  // MediatorDetail's MoM/Booking charts landed on Cases unfiltered —
  // user saw every mediator's cases for the date range instead of just
  // the current mediator's.
  const initialMediatorId = searchParams.get('mediatorId') || '';
  const [selectedMediator, setSelectedMediator] = useState(initialMediatorId);

  // Sequence guard for the fetch effect below — see comment there.
  const casesSeqRef = useRef(0);
  // Drill-downs from the Booking Trend chart can pass dateField (the
  // basis the chart bucketed on) and caseType (Private/MCLA). The BE
  // forwards these to the cases query; we just thread them through.
  const initialDateField = searchParams.get('dateField') || '';
  const initialCaseType = searchParams.get('caseType') || '';
  // Default to 'upcoming' (Scale parity — its Cases page defaults to
  // filterOptions[1] = 'upcoming'), unless deep-linked with explicit dates.
  const [dateFilter, setDateFilter] = useState(initialStart && initialEnd ? 'custom' : 'upcoming');
  const [customStart, setCustomStart] = useState(initialStart);
  const [customEnd, setCustomEnd] = useState(initialEnd);
  // Date-basis toggle (Mediation Date vs Booking Date). Seeded from the
  // URL (?dateField=…) so chart drill-downs land on the right basis, and
  // mutable via the FilterBar pill so users can pivot in-page.
  const [dateFieldFilter, setDateFieldFilter] = useState(initialDateField || 'mediation_date');

  const { widths: colWidths, setWidth: setColWidth } = useResizableColumns(
    'cases.list',
    { mediator: 220, caseName: 320, date: 160, bookingDate: 150, plaintiff: 240 }
  );
  // Server-driven sort.  Header components expect { key, direction }; we
  // map our (sortBy, sortDir) state into that shape, and on header click
  // toggle direction or switch keys.  Page resets to 1 so the user doesn't
  // land on a page that no longer exists under the new ordering.
  //
  // Maps FE column key → backend cases-table column.  Columns without a
  // BE-side equivalent (plaintiff/defendant progress pills, mediator name
  // which lives in a separate table) fall through and the header is
  // rendered non-sortable below.
  const FE_TO_BE_SORT = {
    caseName: 'case_name',
    date: 'mediation_date',
    bookingDate: 'created_at',
  };
  const BE_TO_FE_SORT = Object.fromEntries(
    Object.entries(FE_TO_BE_SORT).map(([fe, be]) => [be, fe])
  );
  const tableSort = {
    key: BE_TO_FE_SORT[sortBy] || 'date',
    // SortableHeader/useSortableData read `dir` (not `direction`) — the wrong
    // key left the active sort indicator permanently inactive so sorting looked
    // broken (no arrow / no feedback on the clicked column).
    dir: sortDir === 'asc' ? 'asc' : 'desc',
  };
  const requestTableSort = (feKey) => {
    const beKey = FE_TO_BE_SORT[feKey];
    if (!beKey) return; // non-sortable column
    if (sortBy === beKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(beKey);
      // Date columns (Mediation Date / Booking Date) default to most-recent-
      // first (desc); text columns (Case Name) default to A→Z (asc).
      setSortDir(beKey === 'mediation_date' || beKey === 'created_at' ? 'desc' : 'asc');
    }
    setCurrentPage(1);
  };
  // Sort happens server-side; the rows we receive are already in order.
  const sortedCases = casesData;

  const getDateRange = (filter) => dateRangeForFilter(filter, customStart, customEnd);

  const handleCaseStatusChange = async (caseId, newStatus) => {
    if (newStatus === 'cancelled') {
      if (!window.confirm('Are you sure you want to cancel this case?')) {
        setActionMenuId(null);
        return;
      }
    }
    try {
      await api.patch(`/cases/${caseId}/status`, { status: newStatus });
      // Re-fetch cases
      const { startDate: sd, endDate: ed } = getDateRange(dateFilter);
      const res = await api.get(`/cases?page=${currentPage}&limit=${pageSize}&sortBy=${encodeURIComponent(sortBy)}&sortDir=${encodeURIComponent(sortDir)}${searchQuery ? `&search=${searchQuery}` : ''}${selectedMediator ? `&mediatorId=${selectedMediator}` : ''}${sd ? `&startDate=${sd}` : ''}${ed ? `&endDate=${ed}` : ''}${dateFieldFilter && dateFieldFilter !== 'mediation_date' ? `&dateField=${encodeURIComponent(dateFieldFilter)}` : ''}`);
      const mapped = (res.data.data || []).map(c => ({
        id: c.id,
        caseId: String(c.id),
        mediator: c.mediatorName || 'Unassigned',
        briefText: c.briefText || 'Brief',
        caseName: c.caseName || `Case ${c.caseNumber || c.id || ''}`,
        date: c.date ? new Date(c.date + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '',
        dateRaw: c.date || '',
        // Booking date = `created_at` (when the case was booked into the
        // system). Surfaced alongside Mediation Date so users coming from
        // a Booking Trend chart drill-down can see the booking date that
        // landed each case in the chosen window.
        bookingDate: fmtBookingDate(c.createdAt),
        bookingDateRaw: c.createdAt || '',
        plaintiff: {
          onboarding: c.plaintiff?.onboarding || 'grey',
          payment: c.plaintiff?.payment || 'grey',
          brief: c.plaintiff?.brief || 'grey',
          onboardingStatus: c.plaintiff?.onboardingStatus,
          paymentStatus: c.plaintiff?.paymentStatus,
          briefStatus: c.plaintiff?.briefStatus,
        },
        defendant: {
          onboarding: c.defendant?.onboarding || 'grey',
          payment: c.defendant?.payment || 'grey',
          brief: c.defendant?.brief || 'grey',
          onboardingStatus: c.defendant?.onboardingStatus,
          paymentStatus: c.defendant?.paymentStatus,
          briefStatus: c.defendant?.briefStatus,
        },
      }));
      setCasesData(mapped);
      setTotalEntries(res.data.total || 0);
      setTotalPages(res.data.pages || 1);
    } catch (err) {
      console.error('Failed to update case status:', err);
    }
    setActionMenuId(null);
  };

  // Mediators dropdown is filter-independent — load once on mount.
  useEffect(() => {
    api.get('/mediators?limit=200')
      .then(res => {
        const list = (res.data?.data || []).filter(m => m.name && m.name.trim());
        setMediatorsList(list);
      })
      .catch(console.error);
  }, []);

  // Single fetch effect for both KPI cards (/dashboard) and the cases
  // list. We now send mediator + search + date range to BOTH endpoints
  // so the tiles react to the filter the same way the Dashboard page's
  // tiles do. Note: /cases filters by mediation_date and /dashboard
  // filters by created_at, so the same range may produce different
  // counts — that's intentional, the Cases tiles are "what's been
  // booked/created in the chosen period" and the table is "what's
  // happening (mediation date) in the chosen period".
  // Debounced 250ms so search keystrokes don't spam the server.
  //
  // Sequence guard: each effect run bumps reqSeqRef; both response
  // handlers compare against it and bail if a newer effect has
  // already fired. Without this, a slow /cases response arriving
  // after a faster subsequent filter change would overwrite fresh
  // state with stale rows (same root cause Mediators.jsx already
  // handles).
  useEffect(() => {
    const mySeq = ++casesSeqRef.current;
    const handler = setTimeout(() => {
      setLoading(true);
      const { startDate, endDate } = getDateRange(dateFilter);
      const parts = [];
      if (searchQuery) parts.push(`search=${encodeURIComponent(searchQuery)}`);
      if (selectedMediator) parts.push(`mediatorId=${selectedMediator}`);
      if (startDate) parts.push(`startDate=${startDate}`);
      if (endDate) parts.push(`endDate=${endDate}`);
      // Forward dateField to BOTH endpoints so the KPI tiles + table
      // narrow on the same date column the radio picked. /dashboard honors
      // it for Total/Active Cases; /cases honors it for the list filter.
      const dashboardParts = parts.slice();
      if (dateFieldFilter && dateFieldFilter !== 'mediation_date') {
        dashboardParts.push(`dateField=${encodeURIComponent(dateFieldFilter)}`);
      }
      // Forward case-type so the KPI tiles (incl. Case Manager Fee) narrow on
      // the same case-type the table does.
      if (initialCaseType) dashboardParts.push(`caseType=${encodeURIComponent(initialCaseType)}`);
      const dashboardQs = dashboardParts.join('&');
      const caseParts = parts.slice();
      if (dateFieldFilter) caseParts.push(`dateField=${encodeURIComponent(dateFieldFilter)}`);
      if (initialCaseType) caseParts.push(`caseType=${encodeURIComponent(initialCaseType)}`);
      const casesQs = caseParts.join('&');
      api.get(`/dashboard${dashboardQs ? `?${dashboardQs}` : ''}`)
        .then(res => {
          if (mySeq !== casesSeqRef.current) return;
          const data = res.data?.data || res.data || {};
          setDashboardStats(data);
        })
        .catch(console.error);
      api.get(`/cases?page=${currentPage}&limit=${pageSize}&sortBy=${encodeURIComponent(sortBy)}&sortDir=${encodeURIComponent(sortDir)}${casesQs ? `&${casesQs}` : ''}`)
        .then(res => {
        if (mySeq !== casesSeqRef.current) return;
        const mapped = (res.data.data || []).map(c => ({
          id: c.id,
          caseId: String(c.id),
          mediator: c.mediatorName || 'Unassigned',
          briefText: c.briefText || 'Brief',
          caseName: c.caseName || `Case ${c.caseNumber || c.id || ''}`,
          date: c.date ? new Date(c.date + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '',
          // dateRaw is the ISO string the date-sort accessor reads. Without
          // it, sortable column "Mediator Date" gets an undefined accessor
          // value for every row and the sort becomes a no-op (all nulls).
          dateRaw: c.date || '',
          // Booking Date column — `c.createdAt` is when the case was created
          // (= when it was booked). Mirrors the bookingDate field built in
          // handleCaseStatusChange's row-map.
          bookingDate: fmtBookingDate(c.createdAt),
          bookingDateRaw: c.createdAt || '',
          plaintiff: {
            onboarding: c.plaintiff?.onboarding || 'grey',
            payment: c.plaintiff?.payment || 'grey',
            brief: c.plaintiff?.brief || 'grey',
            onboardingStatus: c.plaintiff?.onboardingStatus,
            paymentStatus: c.plaintiff?.paymentStatus,
            briefStatus: c.plaintiff?.briefStatus,
          },
          defendant: {
            onboarding: c.defendant?.onboarding || 'grey',
            payment: c.defendant?.payment || 'grey',
            brief: c.defendant?.brief || 'grey',
            onboardingStatus: c.defendant?.onboardingStatus,
            paymentStatus: c.defendant?.paymentStatus,
            briefStatus: c.defendant?.briefStatus,
          },
        }));
        setCasesData(mapped);
        setTotalEntries(res.data.total || 0);
        setTotalPages(res.data.pages || 1);
      })
      .catch(console.error)
      .finally(() => {
        if (mySeq !== casesSeqRef.current) return;
        setLoading(false);
      });
    }, 250);
    return () => clearTimeout(handler);
  }, [currentPage, pageSize, sortBy, sortDir, searchQuery, selectedMediator, dateFilter, customStart, customEnd, dateFieldFilter, initialDateField, initialCaseType]);

  return (
    <div className="flex flex-col bg-white">
      {/* ── Header Bar (60px) ── */}
      <div
        className="flex items-center justify-between bg-white border-b border-[#e5e5e5]"
        style={{ height: 60, padding: '14px 24px' }}
      >
        <span className="text-sm font-semibold text-[#0e1e20] leading-[1.5]" style={{ fontFamily: 'Inter Tight' }}>
          Mediation Cases
        </span>
        <div className="flex items-center gap-4">
          <div className="w-8 h-8 rounded-full bg-[#115e59] flex items-center justify-center">
            <span className="text-xs font-semibold text-white" style={{ fontFamily: 'Inter' }}>{initials}</span>
          </div>
        </div>
      </div>

      {/* ── Filters (above tiles, drives both /dashboard tiles and /cases table) ── */}
      <div style={{ padding: '20px 24px 0 24px' }}>
        <FilterBar
          searchValue={searchQuery}
          onSearchChange={(v) => { setSearchQuery(v); setCurrentPage(1); }}
          searchPlaceholder="Search by case ID, name, or mediator..."
          dateValue={dateFilter}
          onDateChange={(v) => { setDateFilter(v); setCurrentPage(1); }}
          dateOptions={DEFAULT_DATE_PRESETS}
          dateDefaultValue="all"
          customStart={customStart}
          customEnd={customEnd}
          onCustomDateChange={(s, e) => { setCustomStart(s); setCustomEnd(e); setCurrentPage(1); }}
          mediatorValue={selectedMediator}
          onMediatorChange={(v) => { setSelectedMediator(v); setCurrentPage(1); }}
          mediators={mediatorsList}
          dateFieldValue={dateFieldFilter}
          onDateFieldChange={(v) => { setDateFieldFilter(v); setCurrentPage(1); }}
          dateFieldOptions={[
            { value: 'mediation_date', label: 'Mediation Date' },
            { value: 'created_at',     label: 'Booking Date' },
          ]}
        />
      </div>

      {/* ── KPI Metrics ── */}
      {(() => {
        // Period chip text — same logic the Dashboard uses. "All time"
        // when no date filter, the preset's display label otherwise, or
        // the literal custom range when the user picked one.
        const presetLabel = (DEFAULT_DATE_PRESETS.find((p) => p.value === dateFilter) || {}).label;
        const periodLabel =
          dateFilter === 'custom' && (customStart || customEnd)
            ? `${customStart || '…'} – ${customEnd || '…'}`
            : (presetLabel || 'All time');
        return (
      <div className="flex gap-3" style={{ padding: '20px 24px 26px 24px' }}>
        <KPICard
          title="Total Cases"
          periodLabel={periodLabel}
          value={totalEntries != null ? totalEntries : (loading ? '\u2014' : '0')}
          change={dashboardStats?.change?.totalCases}
        />
        <KPICard
          title="Upcoming Cases"
          periodLabel={periodLabel}
          value={dashboardStats?.upcomingCasesCount ?? '\u2014'}
        />
        <KPICard
          title="Completed Cases"
          periodLabel={periodLabel}
          value={dashboardStats?.completedCases ?? '\u2014'}
          change={dashboardStats?.change?.completedCases}
        />
        <KPICard
          title="Case Manager Fee"
          periodLabel={periodLabel}
          value={dashboardStats?.cmFee != null ? `$${Math.round(Number(dashboardStats.cmFee) || 0).toLocaleString('en-US')}` : '\u2014'}
          change={dashboardStats?.change?.cmFee}
        />
      </div>
        );
      })()}

      {/* ── Table Section ── */}
      <div className="relative" style={{ padding: '24px 24px 32px 24px' }}>
        {loading && (
          <div className="absolute inset-0 bg-white/80 z-10 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-[#115e59] border-t-transparent rounded-full animate-spin" />
              <span className="text-sm text-[#5b5a64] font-['Inter']">Loading...</span>
            </div>
          </div>
        )}
        {/* Table */}
        <div className="rounded-xl bg-white overflow-hidden">
          {/* Table Header */}
          <div className="flex items-center bg-[#f9fafb] border border-[#f0f0f0] rounded-t-xl" style={{ height: 45 }}>
            <div className="flex items-center h-full px-3 relative" style={{ width: colWidths.mediator }}>
              {/* Mediator name lives on a separate table — BE-side sort isn't
                  wired. Render as a plain header so we don't pretend to sort
                  while only re-ordering the visible page. */}
              <span className="text-xs font-semibold text-[#5b5a64] tracking-[0.3px]" style={{ fontFamily: 'Inter' }}>Mediator</span>
              <ResizeHandle onResize={setColWidth('mediator')} getCurrentWidth={() => colWidths.mediator} />
            </div>
            <div className="flex items-center h-full px-3 relative min-w-0" style={{ width: colWidths.caseName }}>
              <SortableHeader sortKey="caseName" sort={tableSort} onSort={requestTableSort} className="text-xs font-medium text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>Case Name</SortableHeader>
              <ResizeHandle onResize={setColWidth('caseName')} getCurrentWidth={() => colWidths.caseName} />
            </div>
            <div className="flex items-center h-full px-3 relative" style={{ width: colWidths.date }}>
              <SortableHeader sortKey="date" sort={tableSort} onSort={requestTableSort} className="text-xs font-medium text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>Mediation Date</SortableHeader>
              <ResizeHandle onResize={setColWidth('date')} getCurrentWidth={() => colWidths.date} />
            </div>
            <div className="flex items-center h-full px-3 relative" style={{ width: colWidths.bookingDate }}>
              <SortableHeader sortKey="bookingDate" sort={tableSort} onSort={requestTableSort} className="text-xs font-medium text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>Booking Date</SortableHeader>
              <ResizeHandle onResize={setColWidth('bookingDate')} getCurrentWidth={() => colWidths.bookingDate} />
            </div>
            <div className="flex items-center h-full px-3 relative" style={{ width: colWidths.plaintiff }}>
              <span className="text-xs font-semibold text-[#5b5a64] tracking-[0.3px]" style={{ fontFamily: 'Inter' }}>Plaintiff Status</span>
              <ResizeHandle onResize={setColWidth('plaintiff')} getCurrentWidth={() => colWidths.plaintiff} />
            </div>
            <div className="flex-1 flex items-center h-full px-3 min-w-0">
              <span className="text-xs font-semibold text-[#5b5a64] tracking-[0.3px]" style={{ fontFamily: 'Inter' }}>Defendant Status</span>
            </div>
          </div>

          {/* Table Rows */}
          {!loading && casesData.length === 0 && (
            <div className="flex items-center justify-center" style={{ height: 120 }}>
              <span className="text-sm text-[#5b5a64] font-['Inter']">No cases found</span>
            </div>
          )}
          {sortedCases.map((row) => (
            <div
              key={row.id}
              className="flex items-center border-b border-[#f0f0f0] cursor-pointer hover:bg-gray-50 transition-colors"
              style={{ height: 48 }}
              onClick={() => navigate(`/cases/${row.id}`)}
            >
              <div className="flex items-center h-full px-3 min-w-0" style={{ width: colWidths.mediator }}>
                <span className="text-[13px] font-normal text-[#0e1e20] truncate" style={{ fontFamily: 'Inter' }}>{row.mediator}</span>
              </div>
              <div className="flex items-center h-full px-3 min-w-0" style={{ width: colWidths.caseName }}>
                <span className="text-[13px] font-normal text-[#0e1e20] truncate" style={{ fontFamily: 'Inter' }}>{row.caseName}</span>
              </div>
              <div className="flex items-center h-full px-3 min-w-0" style={{ width: colWidths.date }}>
                <span className="text-[13px] font-normal text-[#0e1e20]" style={{ fontFamily: 'Inter' }}>{row.date}</span>
              </div>
              <div className="flex items-center h-full px-3 min-w-0" style={{ width: colWidths.bookingDate }}>
                <span className="text-[13px] font-normal text-[#0e1e20]" style={{ fontFamily: 'Inter' }}>{row.bookingDate}</span>
              </div>
              <div className="flex items-center gap-3 h-full px-3 min-w-0" style={{ width: colWidths.plaintiff }}>
                <StatusCell status={row.plaintiff} briefLabel={row.briefText} />
              </div>
              <div className="flex-1 flex items-center gap-3 h-full px-3 min-w-0">
                <StatusCell status={row.defendant} briefLabel={row.briefText} />
              </div>
            </div>
          ))}

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalEntries={totalEntries}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
          />
        </div>
      </div>
    </div>
  );
}
